import React, { useState } from 'react';
import { X, Settings, Bell, Lock, Globe, Moon, Shield, Eye, Mail, Smartphone } from 'lucide-react';
import modalsBg from '../assets/Modals.jpg';

const SettingsModal = ({ isOpen, onClose, user }) => {
  const [activeTab, setActiveTab] = useState('notifications');
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    smsNotifications: false,
    newMessages: true,
    propertyUpdates: true,
    marketingEmails: false,
    darkMode: false,
    language: 'en',
    twoFactor: false,
    publicProfile: true
  });

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const tabs = [
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy & Security', icon: Shield },
    { id: 'preferences', label: 'Preferences', icon: Settings }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-4xl h-[92vh] sm:h-auto sm:max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 flex items-start sm:items-center justify-between gap-4" style={{ backgroundImage: `url(${modalsBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 sm:w-7 sm:h-7 text-white flex-shrink-0" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Settings</h2>
              <p className="text-blue-100 text-xs sm:text-sm">Manage your account preferences</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col sm:flex-row flex-1 overflow-hidden min-h-0">
          {/* Sidebar Tabs */}
          <div className="w-full sm:w-64 sm:border-r border-gray-200 bg-gray-50 p-3 sm:p-4 overflow-x-auto">
            <nav className="flex sm:block gap-2 sm:space-y-2 min-w-max sm:min-w-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex sm:w-full items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="font-medium text-sm sm:text-base">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Settings Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-8 min-h-0">
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Notification Preferences</h3>
                  <p className="text-sm text-gray-500 mb-6">Choose how you want to be notified</p>
                </div>

                {/* Notification Channels */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 sm:p-5 border border-blue-200">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Email Notifications</h4>
                          <p className="text-sm text-gray-600">Receive updates via email</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.emailNotifications}
                          onChange={() => handleToggle('emailNotifications')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 border border-gray-300 opacity-60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Bell className="w-5 h-5 text-gray-500" />
                        <div>
                          <h4 className="font-semibold text-gray-700">Push Notifications</h4>
                          <p className="text-sm text-gray-500">Currently unavailable</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-not-allowed">
                        <input
                          type="checkbox"
                          checked={settings.pushNotifications}
                          disabled
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5"></div>
                      </label>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 border border-gray-300 opacity-60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-gray-500" />
                        <div>
                          <h4 className="font-semibold text-gray-700">SMS Notifications</h4>
                          <p className="text-sm text-gray-500">Currently unavailable</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-not-allowed">
                        <input
                          type="checkbox"
                          checked={settings.smsNotifications}
                          disabled
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Notification Types */}
                <div className="mt-8">
                  <h4 className="font-semibold text-gray-900 mb-4">What to notify me about</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-700">New messages</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.newMessages}
                          onChange={() => handleToggle('newMessages')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-700">Property updates</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.propertyUpdates}
                          onChange={() => handleToggle('propertyUpdates')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-700">Marketing emails</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.marketingEmails}
                          onChange={() => handleToggle('marketingEmails')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Privacy & Security</h3>
                  <p className="text-sm text-gray-500 mb-6">Control your privacy and security settings</p>
                </div>

                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 border border-gray-300 opacity-60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Lock className="w-5 h-5 text-gray-500" />
                        <div>
                          <h4 className="font-semibold text-gray-700">Two-Factor Authentication</h4>
                          <p className="text-sm text-gray-500">Currently unavailable</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-not-allowed">
                        <input
                          type="checkbox"
                          checked={settings.twoFactor}
                          disabled
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5"></div>
                      </label>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-xl p-4 sm:p-5 border border-indigo-200">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-indigo-600" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Public Profile</h4>
                          <p className="text-sm text-gray-600">Your profile is visible to others</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-not-allowed">
                        <input
                          type="checkbox"
                          checked={settings.publicProfile}
                          disabled
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-indigo-600 rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white after:border after:rounded-full after:h-5 after:w-5 after:translate-x-full"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Preferences</h3>
                  <p className="text-sm text-gray-500 mb-6">Customize your experience</p>
                </div>

                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 sm:p-5 border border-gray-300 opacity-60">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Moon className="w-5 h-5 text-gray-500" />
                        <div>
                          <h4 className="font-semibold text-gray-700">Dark Mode</h4>
                          <p className="text-sm text-gray-500">Currently unavailable</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-not-allowed">
                        <input
                          type="checkbox"
                          checked={settings.darkMode}
                          disabled
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5"></div>
                      </label>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-4 sm:p-5 border border-teal-200">
                    <div className="flex items-center gap-3 mb-3">
                      <Globe className="w-5 h-5 text-teal-600" />
                      <div>
                        <h4 className="font-semibold text-gray-900">Language</h4>
                        <p className="text-sm text-gray-600">English (Default)</p>
                      </div>
                    </div>
                    <select
                      value={settings.language}
                      disabled
                      className="w-full px-4 py-2.5 border border-teal-300 rounded-xl outline-none bg-gray-100 cursor-not-allowed text-gray-700"
                    >
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-8 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
